import Link from 'next/link';
import { User } from 'lucide-react';
import { useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { RoleSwitcher } from '@/src/features/role-switcher';
import { useRole } from '@/src/shared/lib/hooks/use-role';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { getUserActors, switchOrCreateActorByType } from '@/src/entities/user/api';
import { ActorType } from '@hummii/shared-types';
import { LogoutButton } from '@/src/features/auth';
import { mapRouteForActor } from '@/src/shared/lib/utils/route-mapper';

interface UserMenuProps {
  user: {
    id: string;
    name?: string;
    email?: string;
  };
  locale: string;
  onLogout?: () => void;
}

export function UserMenu({ user, locale }: UserMenuProps) {
  const { activeActorType } = useRole();
  const { activeActor, setActiveActor, setAvailableActors } = useAuthStore();
  const router = useRouter();
  const pathname = usePathname();
  const [isSwitching, setIsSwitching] = useState(false);

  // Profile URL - new unified route that auto-detects role
  const profileUrl = `/${locale}/profile`;

  /**
   * Handle actor switch with smart route mapping
   * Creates actor if doesn't exist, then switches to it
   *
   * Uses mapRouteForActor which now:
   * - Preserves public pages (home, search, categories, profile)
   * - Preserves query params and hash (e.g., ?tab=orders)
   * - Maps actor-specific pages to their equivalents
   */
  const handleActorSwitch = async (actorType: ActorType) => {
    if (isSwitching) return;

    setIsSwitching(true);
    try {
      // Call backend API to switch/create actor
      const response = await switchOrCreateActorByType(actorType);

      // Update local state with new active actor
      setActiveActor(response.actor);

      // Refresh available actors from backend
      const { actors } = await getUserActors();
      setAvailableActors(actors);

      // Smart route mapping: preserve current page context and query params
      const targetPath = pathname
        ? mapRouteForActor(pathname, actorType)
        : `/${locale}/${actorType === ActorType.CLIENT ? 'client' : 'worker'}/dashboard`;

      console.log('Actor switched successfully:', {
        newActor: response.actor,
        currentPath: pathname,
        navigatingTo: targetPath,
      });

      // Navigate to appropriate page for new actor type
      router.push(targetPath);
    } catch (error) {
      console.error('Failed to switch actor:', error);
      alert('Failed to switch role. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Actor Switcher - always shown for authenticated users */}
      <RoleSwitcher
        activeRole={activeActorType ?? ActorType.CLIENT}
        onRoleChange={handleActorSwitch}
        locale={locale}
      />

      {/* User Profile Link - navigates to active actor's profile */}
      <Link
        href={profileUrl}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-desktop-body-sm font-medium text-text-primary transition-colors hover:bg-background-2"
      >
        <User className="h-5 w-5" />
        <span>Profile</span>
      </Link>

      {/* Logout Button */}
      <LogoutButton locale={locale} label="Logout" />
    </div>
  );
}
